import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Input, Textarea, Select, Card } from "../components/ui";
import FileUpload from "../components/ui/FileUpload";

const TIPOLOGIA_RICHIESTA_OPTIONS = [
  { value: 100000000, label: "Ordinario" },
  { value: 100000001, label: "Straordinario" },
];

function NuovaRichiestaPage() {
  const { apiRequest, getAccessToken } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [rsaList, setRsaList] = useState([]);
  const [tipologie, setTipologie] = useState([]);

  const [allegati, setAllegati] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [guideDisponibili, setGuideDisponibili] = useState([]);
  const [guideFotoPreviews, setGuideFotoPreviews] = useState({});
  const guideFotoPreviewsRef = useRef({});
  const [lightboxFoto, setLightboxFoto] = useState(null);

  const [qrModal, setQrModal] = useState(false);
  const [qrData, setQrData] = useState(null); // { token, url }
  const [generatingQr, setGeneratingQr] = useState(false);

  const [form, setForm] = useState({
    oggetto: "",
    rsaId: "",
    tipologiaRichiesta: "",
    tipologiaInterventoId: "",
    note: "",
  });

  // Carica RSA e catalogo tipologie intervento (globale) all'avvio
  useEffect(() => {
    (async () => {
      try {
        const res = await apiRequest("/rsa");
        setRsaList(res?.value || []);
      } catch {
        showToast("Errore nel caricamento delle RSA", "error");
      } finally {
        setLoadingData(false);
      }

      try {
        const tipRes = await apiRequest("/tipologie-intervento");
        const tutte = tipRes?.value || [];
        const generiche = tutte.filter((t) => t.generica);
        const altre = tutte
          .filter((t) => !t.generica)
          .sort((a, b) => (a.nome || "").localeCompare(b.nome || "", "it"));
        setTipologie([...altre, ...generiche]);
      } catch {
        setTipologie([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Guide di autorisoluzione per la tipologia selezionata (suggerimento non bloccante)
  useEffect(() => {
    if (!form.tipologiaInterventoId) {
      setGuideDisponibili([]);
      return;
    }
    (async () => {
      try {
        const res = await apiRequest(
          `/guide-autorisoluzione/per-tipologia/${form.tipologiaInterventoId}`,
        );
        setGuideDisponibili(res?.value || []);
      } catch {
        setGuideDisponibili([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.tipologiaInterventoId]);

  // Carica le foto degli step come blob autenticati (il webUrl SharePoint non è
  // raggiungibile direttamente da <img> senza una sessione Microsoft attiva)
  useEffect(() => {
    const stepsConFoto = guideDisponibili
      .flatMap((g) => g.steps || [])
      .filter((s) => s.foto);
    if (!stepsConFoto.length) return;

    let cancelled = false;
    (async () => {
      const token = await getAccessToken();
      for (const s of stepsConFoto) {
        if (cancelled) break;
        if (guideFotoPreviewsRef.current[s.id]) continue;
        try {
          const res = await fetch(
            `/api/guide-autorisoluzione/foto/${s.foto.id}`,
            {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            },
          );
          if (!res.ok || cancelled) continue;
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          guideFotoPreviewsRef.current[s.id] = url;
          if (!cancelled)
            setGuideFotoPreviews((prev) => ({ ...prev, [s.id]: url }));
        } catch {
          /* ignora errori preview singoli */
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [guideDisponibili, getAccessToken]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(guideFotoPreviewsRef.current).forEach(URL.revokeObjectURL);
    };
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      return next;
    });
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.rsaId) e.rsaId = "Seleziona una RSA";
    if (form.tipologiaRichiesta === "")
      e.tipologiaRichiesta = "Seleziona la tipologia";
    if (!form.tipologiaInterventoId)
      e.tipologiaInterventoId = "Seleziona la tipologia intervento";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = new FormData();
      payload.append("rsaId", form.rsaId);
      payload.append("tipologiaRichiesta", Number(form.tipologiaRichiesta));
      if (form.oggetto) payload.append("oggetto", form.oggetto);
      if (form.tipologiaInterventoId)
        payload.append("tipologiaInterventoId", form.tipologiaInterventoId);
      if (form.note) payload.append("note", form.note);
      allegati.forEach((f) => payload.append("allegati", f));

      const result = await apiRequest("/richieste-intervento", {
        method: "POST",
        body: payload,
      });
      showToast("Richiesta inserita e inviata in approvazione", "success");
      navigate(
        result?.id
          ? `/richieste-intervento/${result.id}`
          : "/richieste-intervento",
      );
    } catch (err) {
      showToast(
        err.message || "Errore nell'inserimento della richiesta",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateQr = async () => {
    setGeneratingQr(true);
    try {
      const res = await apiRequest("/richieste-intervento/magic-token", {
        method: "POST",
      });
      setQrData(res);
      setQrModal(true);
    } catch {
      showToast("Errore nella generazione del QR", "error");
    } finally {
      setGeneratingQr(false);
    }
  };

  if (loadingData) {
    return <div className="p-6 text-gray-500">Caricamento…</div>;
  }

  const optLabel = (o) => o.nome || o.name || o.nomeCompleto || "—";

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Nuova richiesta di intervento
          </h1>
          <p className="text-gray-500 mt-1">
            La richiesta verrà inviata in approvazione prima dell'invio al
            fornitore.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={handleGenerateQr}
          loading={generatingQr}
          title="Genera QR per inserimento da smartphone"
        >
          📱 QR Mobile
        </Button>
      </div>

      {/* QR Modal */}
      {qrModal && qrData && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setQrModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">QR Mobile</h2>
              <button
                onClick={() => setQrModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-5">
              Inquadra il QR con lo smartphone per inserire una nuova richiesta
              senza login. Valido per <strong>24 ore</strong>.
            </p>
            <div className="flex justify-center bg-white p-3 rounded-xl border border-gray-100">
              <QRCode value={qrData.url} size={220} />
            </div>
            <p className="text-xs text-gray-400 mt-3 text-center break-all">
              {qrData.url}
            </p>
            <Button
              className="mt-4 w-full"
              variant="outline"
              onClick={() => {
                navigator.clipboard?.writeText(qrData.url);
                showToast("Link copiato!", "success");
              }}
            >
              📋 Copia link
            </Button>
          </div>
        </div>
      )}

      <Card>
        <form onSubmit={handleSubmit} className="space-y-5">
          <Input
            label="Oggetto richiesta"
            value={form.oggetto}
            onChange={(e) => handleChange("oggetto", e.target.value)}
            placeholder="Opzionale — generato automaticamente se vuoto"
            helper="Breve descrizione sintetica della richiesta"
          />

          <Select
            label="RSA"
            required
            value={form.rsaId}
            onChange={(e) => handleChange("rsaId", e.target.value)}
            error={errors.rsaId}
          >
            <option value="">Seleziona RSA…</option>
            {rsaList.map((r) => (
              <option key={r.id} value={r.id}>
                {optLabel(r)}
              </option>
            ))}
          </Select>

          <Select
            label="Tipologia richiesta"
            required
            value={form.tipologiaRichiesta}
            onChange={(e) => handleChange("tipologiaRichiesta", e.target.value)}
            error={errors.tipologiaRichiesta}
          >
            <option value="">Seleziona tipologia…</option>
            {TIPOLOGIA_RICHIESTA_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>

          <Select
            label="Tipologia intervento"
            required
            value={form.tipologiaInterventoId}
            onChange={(e) =>
              handleChange("tipologiaInterventoId", e.target.value)
            }
            error={errors.tipologiaInterventoId}
          >
            <option value="">Seleziona tipologia…</option>
            {tipologie.map((t) => (
              <option key={t.id} value={t.id}>
                {optLabel(t)}
              </option>
            ))}
          </Select>

          {/* Guida di autorisoluzione — suggerimento non bloccante */}
          {guideDisponibili.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl">💡</span>
                <div>
                  <p className="text-sm font-semibold text-amber-900">
                    Prova a risolvere da solo prima di inviare la richiesta
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Se il problema si risolve non serve aprire una richiesta al
                    fornitore. Se non si risolve, prosegui pure con
                    l&apos;inserimento qui sotto.
                  </p>
                </div>
              </div>
              {guideDisponibili.map((g) => (
                <div
                  key={g.id}
                  className="bg-white rounded-lg border border-amber-200 p-3"
                >
                  <p className="text-sm font-semibold text-gray-900">
                    {g.titolo}
                  </p>
                  {g.descrizioneBreve && (
                    <p className="text-xs text-gray-500 mb-2">
                      {g.descrizioneBreve}
                    </p>
                  )}
                  <ol className="space-y-2 mt-2">
                    {g.steps.map((s) => (
                      <li
                        key={s.id}
                        className="flex gap-3 text-sm text-gray-700"
                      >
                        <span className="font-semibold text-amber-700">
                          {s.ordine}.
                        </span>
                        <div className="flex-1">
                          <p>{s.testo}</p>
                          {s.foto && guideFotoPreviews[s.id] && (
                            <button
                              type="button"
                              onClick={() =>
                                setLightboxFoto({
                                  url: guideFotoPreviews[s.id],
                                  nome: s.foto.nome || `Step ${s.ordine}`,
                                })
                              }
                              className="mt-2 block w-full max-w-sm cursor-zoom-in"
                              title="Clicca per ingrandire"
                            >
                              <img
                                src={guideFotoPreviews[s.id]}
                                alt={s.foto.nome || `Step ${s.ordine}`}
                                className="w-full max-h-56 rounded border border-gray-200 object-contain bg-white hover:opacity-90 transition-opacity"
                              />
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          )}

          <Textarea
            label="Descrizione Intervento"
            value={form.note}
            onChange={(e) => handleChange("note", e.target.value)}
            rows={4}
            placeholder="Descrizione della richiesta, dettagli operativi…"
          />

          <FileUpload
            label="Foto allegati (opzionale)"
            accept="image/*"
            multiple
            maxSize={20}
            value={allegati}
            onChange={(files) =>
              setAllegati(Array.isArray(files) ? files.slice(0, 5) : [])
            }
            helper="Massimo 5 foto, 20 MB ciascuna. Verranno allegate all'email inviata al fornitore."
          />

          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/richieste-intervento")}
            >
              Annulla
            </Button>
            <Button type="submit" loading={submitting} disabled={submitting}>
              Inserisci richiesta
            </Button>
          </div>
        </form>
      </Card>

      {/* Lightbox foto guida */}
      {lightboxFoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightboxFoto(null)}
        >
          <div
            className="relative max-w-3xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxFoto.url}
              alt={lightboxFoto.nome}
              className="w-full max-h-[85vh] object-contain rounded-lg shadow-xl bg-white"
            />
            <button
              type="button"
              onClick={() => setLightboxFoto(null)}
              className="absolute -top-3 -right-3 h-8 w-8 rounded-full bg-white text-gray-700 shadow flex items-center justify-center text-lg leading-none"
              title="Chiudi"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default NuovaRichiestaPage;
